<template>
<div class="nch--custom-page" :class="pageClasses" :style="isHtmlMode ? {} : rootStyle">
    <!-- Preview banner -->
    <div v-if="isPreview" class="nch--preview-banner">
        <i class="fas fa-eye mr-1"></i>
        Modo Preview — esta página ainda pode ser rascunho
    </div>

    <!-- HTML completo: zero interferência, só o iframe -->
    <custom-html-page
        v-if="isHtmlMode"
        :config="page.config || {}"
    ></custom-html-page>

    <!-- Outros tipos: hero + conteúdo com aparência customizada -->
    <template v-else>
        <!-- Hero section -->
        <div
            v-if="hasCover || hasTitle"
            class="nch--custom-page-hero"
            :style="heroStyle"
        >
            <div class="nch--custom-page-hero__overlay" :style="overlayStyle"></div>
            <div class="nch--custom-page-hero__content">
                <img
                    v-if="page.cover_upload && page.cover_upload.host"
                    :src="logoUrl"
                    class="nch--custom-page-hero__logo"
                    alt=""
                />
                <h1 class="nch--custom-page-hero__title" :style="{ color: titleColor }">
                    {{ pageTitle }}
                </h1>
                <p v-if="pageDescription" class="nch--custom-page-hero__desc" :style="{ color: titleColor }">
                    {{ pageDescription }}
                </p>
            </div>
        </div>

        <div class="nch--page-body">
            <plans-filter-page
                v-if="page.type === 'plans_filter'"
                :config="page.config || {}"
                :page-color="pageColor"
                :layout="layout"
                :categories="categories"
                :plan-facets="planFacets"
                :available-years="availableYears"
            ></plans-filter-page>

            <gallery-page
                v-else-if="page.type === 'gallery'"
                :config="page.config || {}"
                :images="galleryImages"
                :category-galleries="categoryGalleries"
                :plan-galleries="planGalleries"
                :categories="categories"
                :plan-facets="planFacets"
                :available-years="availableYears"
                :page-color="pageColor"
            ></gallery-page>

            <acervo-page
                v-else-if="page.type === 'acervo'"
                :config="page.config || {}"
                :files="acervoFiles"
                :slug="page.slug"
            ></acervo-page>

            <suggestion-page
                v-else-if="page.type === 'suggestion'"
                :config="page.config || {}"
                :slug="page.slug"
            ></suggestion-page>

            <div v-else class="container py-6 text-center grey--text">
                <i class="fas fa-tools fa-2x"></i>
                <p class="mt-2">Tipo de página não disponível ainda</p>
            </div>
        </div>
    </template>
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import PlansFilterPage from './CustomPageTypes/PlansFilterPage';
import CustomHtmlPage from './CustomPageTypes/CustomHtmlPage';
import GalleryPage from './CustomPageTypes/GalleryPage';
import AcervoPage from './CustomPageTypes/AcervoPage';
import SuggestionPage from './CustomPageTypes/SuggestionPage';

export default {
    name: 'CustomPage',
    components: { PlansFilterPage, CustomHtmlPage, GalleryPage, AcervoPage, SuggestionPage },

    props: {
        page: { type: Object, required: true },
        color: { type: Object, required: true },
        layout: { type: Object, required: true },
        categories: { type: Array, default: () => [] },
        plans: { type: Array, default: () => [] },
        planFacets: { type: Array, default: () => [] },
        galleryImages: { type: Array, default: () => [] },
        categoryGalleries: { type: Array, default: () => [] },
        planGalleries: { type: Array, default: () => [] },
        acervoFiles: { type: Array, default: () => [] },
        availableYears: { type: Array, default: () => [] },
        token: { type: String, default: null },
        isPreview: { type: Boolean, default: false },
    },

    computed: {
        ...mapGetters({ language: 'LANGUAGE' }),

        pageColor() {
            return {
                primary: this.page.color_primary || this.color.primary,
                secondary: this.page.color_secondary || this.color.secondary,
            };
        },

        isDarkBackground() {
            return this.isDarkColor(this.page.config && this.page.config.background_color);
        },

        rootStyle() {
            const bg = this.page.config && this.page.config.background_color;
            const primary = this.pageColor.primary || '#333333';
            const isDark = this.isDarkBackground;
            const isDarkPrimary = this.isDarkColor(primary);

            const pageText = isDark ? '#ffffff' : '#212121';
            const pageTextSecondary = isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)';
            const pageBorder = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
            const pageSurface = isDark ? '#262626' : '#f0f0f0';
            const pageSurfaceHover = isDark ? '#333333' : '#e4e4e4';

            const style = {
                '--page-bg': bg || '#ffffff',
                '--page-text': pageText,
                '--page-text-secondary': pageTextSecondary,
                '--page-border': pageBorder,
                '--page-surface': pageSurface,
                '--page-surface-hover': pageSurfaceHover,
                '--page-accent': primary,
                '--page-accent-text': isDarkPrimary ? '#ffffff' : '#212121',
            };

            if (bg) {
                style.backgroundColor = bg;
                style.color = pageText;
            }

            return style;
        },

        isHtmlMode() {
            return this.page.type === 'custom_html';
        },

        pageClasses() {
            return {
                'nch--full-width': !!(this.page.config && this.page.config.full_width),
                'nch--html-mode': this.isHtmlMode,
                'nch--hide-gradient': !!(this.page.config && this.page.config.hide_gradient),
            };
        },

        pageTitle() {
            const t = this.page.translation;
            return t ? t.title : '';
        },

        pageDescription() {
            const t = this.page.translation;
            return t ? t.description : '';
        },

        hasCover() {
            return !!(this.page.cover_upload && this.page.cover_upload.host);
        },

        hasTitle() {
            return !!this.pageTitle;
        },

        coverUrl() {
            if (!this.hasCover) return null;
            const u = this.page.cover_upload;
            return (u.urls && u.urls.original) || `/storage/${u.host}/original/${u.original_file}`;
        },

        logoUrl() {
            if (!this.hasCover) return null;
            const u = this.page.cover_upload;
            return (u.urls && u.urls.md) || `/storage/${u.host}/md/${u.original_file}`;
        },

        heroStyle() {
            const base = {
                backgroundColor: this.pageColor.primary,
                position: 'relative',
                overflow: 'hidden',
            };
            if (this.coverUrl) {
                base.backgroundImage = `url(${this.coverUrl})`;
                base.backgroundSize = 'cover';
                base.backgroundPosition = 'center';
            }
            return base;
        },

        overlayStyle() {
            return {
                backgroundColor: this.pageColor.primary,
                opacity: this.hasCover ? '0.55' : '0',
            };
        },

        titleColor() {
            return this.pageColor.secondary || '#ffffff';
        },
    },

    watch: {
        'page.config': {
            handler: 'updateBodyStyle',
            deep: true,
        },
        'page.color_primary': 'updateBodyStyle',
    },

    beforeMount() {
        if (this.token) {
            localStorage.setItem('nochalks_token', this.token);
            window.axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
        }
    },

    mounted() {
        this.$store.dispatch('GET_SESSION_CURRENCY');
        this.updateBodyStyle();
        // Publica a cor da página p/ o filete do navbar poder acompanhá-la.
        this.$store.commit('SET_PAGE_COLOR', this.pageColor.primary);
    },

    beforeUnmount() {
        this.restoreBodyStyle();
        this.$store.commit('SET_PAGE_COLOR', null);
    },

    methods: {
        isDarkColor(hex) {
            if (!hex) return false;
            const h = hex.replace('#', '');
            if (h.length !== 6 && h.length !== 3) return false;
            let r, g, b;
            if (h.length === 6) {
                r = parseInt(h.substr(0, 2), 16);
                g = parseInt(h.substr(2, 2), 16);
                b = parseInt(h.substr(4, 2), 16);
            } else {
                r = parseInt(h[0] + h[0], 16);
                g = parseInt(h[1] + h[1], 16);
                b = parseInt(h[2] + h[2], 16);
            }
            return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
        },

        updateBodyStyle() {
            // HTML mode: only inject hide_layout CSS if requested, nothing else
            if (this.isHtmlMode) {
                document.body.style.backgroundColor = '';
                const hideLayout = this.page.config && this.page.config.hide_layout;
                let tag = document.getElementById('nch-custom-page-nav-styles');
                if (hideLayout) {
                    if (!tag) {
                        tag = document.createElement('style');
                        tag.id = 'nch-custom-page-nav-styles';
                        document.head.appendChild(tag);
                    }
                    tag.innerHTML = `
                        .navbar.overlay-body { display: none !important; }
                        .nch--footer { display: none !important; }
                        body { padding-top: 0 !important; }
                    `;
                } else if (tag) {
                    tag.remove();
                }
                return;
            }

            const bg = this.page.config && this.page.config.background_color;
            const primary = this.pageColor.primary;
            const secondary = this.pageColor.secondary;

            // Mantém o filete do navbar sincronizado com a cor da página.
            this.$store.commit('SET_PAGE_COLOR', primary);

            if (bg) {
                document.body.style.backgroundColor = bg;
                document.body.style.transition = 'background-color 0.3s';
            } else {
                document.body.style.backgroundColor = '';
            }

            if (!primary) return;

            const isDarkNav = this.isDarkColor(primary);
            const navText = secondary || (isDarkNav ? '#ffffff' : '#212121');

            let tag = document.getElementById('nch-custom-page-nav-styles');
            if (!tag) {
                tag = document.createElement('style');
                tag.id = 'nch-custom-page-nav-styles';
                document.head.appendChild(tag);
            }

            const isFullPageMode = this.page.type === 'custom_html'
                && this.page.config
                && this.page.config.full_page
                && this.page.config.hide_layout;

            tag.innerHTML = `
                /* ── Full page: hide platform layout ── */
                ${isFullPageMode ? `
                .navbar.overlay-body { display: none !important; }
                .nch--footer { display: none !important; }
                .nch--full-html-wrap { margin-top: 0 !important; }
                ` : `
                /* ── Footer ── */
                .nch--footer {
                    background-color: ${primary} !important;
                }
                .nch--footer .nch--footer-section-title,
                .nch--footer .nch--footer-contact-row span,
                .nch--footer .nch--footer-contact-row i,
                .nch--footer .nch--footer-contact-row a,
                .nch--footer .nch--footer-link,
                .nch--footer .nch--footer-social-link i,
                .nch--footer .nch--footer-bottom span,
                .nch--footer .nch--footer-bar-text {
                    color: ${navText} !important;
                }
                .nch--footer .nch--footer-bar {
                    background-color: rgba(0,0,0,${isDarkNav ? '0.2' : '0.06'}) !important;
                }
                `}
            `;
        },

        restoreBodyStyle() {
            document.body.style.backgroundColor = '';
            document.body.style.transition = '';
            const tag = document.getElementById('nch-custom-page-nav-styles');
            if (tag) tag.remove();
        },
    },
};
</script>

<style scoped>
/* HTML mode: zero platform styles leak through */
.nch--html-mode {
    padding: 0 !important;
    margin: 0 !important;
    background: none !important;
}

.nch--preview-banner {
    background: #ff9800;
    color: #fff;
    text-align: center;
    padding: 0.5rem;
    font-size: 0.85rem;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 100;
}

.nch--custom-page-hero {
    min-height: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.nch--custom-page-hero__overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
}

.nch--custom-page-hero__content {
    position: relative;
    z-index: 1;
    text-align: center;
    padding: 3rem 1.5rem;
    max-width: 800px;
}

.nch--custom-page-hero__logo {
    max-height: 80px;
    max-width: 200px;
    object-fit: contain;
    margin-bottom: 1rem;
}

.nch--custom-page-hero__title {
    font-size: 2.2rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    line-height: 1.2;
}

.nch--custom-page-hero__desc {
    font-size: 1.05rem;
    margin: 0;
    line-height: 1.5;
}

/* Gradient transition from hero to body using CSS var */
.nch--custom-page:not(.nch--hide-gradient) .nch--custom-page-hero::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: linear-gradient(to bottom, transparent, var(--page-bg, #ffffff));
    pointer-events: none;
}

/* Full width layout */
.nch--full-width :deep(.container),
.nch--full-width :deep(.nch--custom-html-page.container) {
    max-width: 100% !important;
    padding-left: 2rem !important;
    padding-right: 2rem !important;
}

.nch--full-width :deep(.nch--prose) {
    max-width: 100% !important;
}
</style>
